import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { createRemediationMr, fetchRepoDetail, fetchVulnerabilities, pollRemediationJob } from "../services/cveApi";
import type { DashboardRow, RepoDetail } from "../types";

export const useCveStore = defineStore("cve", () => {
  const rows = ref<DashboardRow[]>([]);
  const loading = ref(false);
  const scanning = ref(false);
  const error = ref<string | null>(null);
  const scanErrors = ref<string[]>([]);
  const activeJobs = ref<Record<string, string>>({});
  const repoDetails = ref<Record<string, RepoDetail>>({});

  const summary = computed(() => {
    const totalCves = rows.value.reduce((sum, row) => sum + row.cveCount, 0);
    const failing = rows.value.filter((row) => row.auditStatus === "failed").length;
    const passing = rows.value.filter((row) => row.auditStatus === "success").length;
    const unknown = rows.value.filter((row) => row.auditStatus === "unknown").length;
    return { totalCves, failing, passing, unknown, repos: rows.value.length };
  });

  const needsGitLabToken = computed(
    () => rows.value.length > 0 && rows.value.every((row) => row.auditStatus === "unknown")
  );

  async function loadDashboard(refresh = false): Promise<void> {
    if (refresh) {
      scanning.value = true;
    } else {
      loading.value = true;
    }
    error.value = null;
    try {
      rows.value = await fetchVulnerabilities(refresh);
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to load vulnerabilities";
    } finally {
      loading.value = false;
      scanning.value = false;
    }
  }

  async function loadRepoDetail(repo: string): Promise<RepoDetail> {
    if (repoDetails.value[repo]) {
      return repoDetails.value[repo];
    }
    const detail = await fetchRepoDetail(repo);
    repoDetails.value[repo] = detail;
    return detail;
  }

  async function openMr(serviceName: string): Promise<string | null> {
    activeJobs.value[serviceName] = "starting";
    try {
      const job = await createRemediationMr(serviceName);
      activeJobs.value[serviceName] = job.status;
      const finalJob = await pollRemediationJob(job.jobId, (updated) => {
        activeJobs.value[serviceName] = updated.status;
      });
      if (finalJob.mrUrl) {
        return finalJob.mrUrl;
      }
      if (finalJob.status === "failed") {
        throw new Error(finalJob.errorMessage || "Remediation failed");
      }
      return null;
    } finally {
      delete activeJobs.value[serviceName];
      delete repoDetails.value[serviceName];
      await loadDashboard(false);
    }
  }

  return {
    rows,
    loading,
    scanning,
    error,
    scanErrors,
    activeJobs,
    repoDetails,
    summary,
    needsGitLabToken,
    loadDashboard,
    loadRepoDetail,
    openMr,
  };
});
